import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import BrushRoundedIcon from '@mui/icons-material/BrushRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded'
import ForumRoundedIcon from '@mui/icons-material/ForumRounded'
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded'
import GroupRoundedIcon from '@mui/icons-material/GroupRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { MiddleEllipsis } from './MiddleEllipsis'
import { MiniLoader } from './MiniLoader'
import { OverflowTip } from './Hover/OverflowTip'
import { AppDialog } from './AppDialog'
import { BottomSheet } from './BottomSheet'
import { Button } from './Button'
import { IconButton } from './IconButton'
import { Pressable } from './Pressable'
import { LeaveForHubDialog } from './LeaveForHubDialog'
import { isLeaveForHubConfirmed, type HubKey } from '../../utils/leaveForHub'
import { useWorkspace } from '../../context/useWorkspace'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import { toConsolePath } from '../../utils/consoleRoutes'
import { uploadWorkspaceFiles, subscribeWorkspaceFiles } from '../../services/storageHubService'
import {
  attachUploadToItem,
  detachProvisionFromItem,
  deleteWorkspaceProvisionPermanently,
  type ItemAttachmentTarget,
} from '../../services/itemAttachmentService'
import { estimateContextCost } from '../../utils/contextCostEstimator'
import { workspaceFileToDataContext } from '../AiGenerator/workspaceAssetContext'
import { subscribeTimelineElements } from '../../services/timelineElementSubscriptionService'
import type { DataContext } from '../../types/aiChatSource'
import type { WorkspaceFileMetadata } from '../../types/schema'
import type { Goal } from '../../types/timeline'
import type { TimelineElement } from '../../context/TimelineContext'
import type { StockItem } from '../../types/stock'
import { FormsResponsesPane } from '../AiGenerator/contextPickerPanes/FormsResponsesPane'
import { DesignsPane } from '../AiGenerator/contextPickerPanes/DesignsPane'
import { ChatsPane } from '../AiGenerator/contextPickerPanes/ChatsPane'
import { NewFileIngestPane } from '../AiGenerator/contextPickerPanes/NewFileIngestPane'
import { GoogleSheetPane } from '../AiGenerator/contextPickerPanes/GoogleSheetPane'
import { ImageUploadPane } from '../AiGenerator/contextPickerPanes/ImageUploadPane'
import { PublicLinkPane } from '../AiGenerator/contextPickerPanes/PublicLinkPane'
import './workspaceItemPicker.css'

export type WorkspaceItemKind =
  | 'goal'
  | 'task'
  | 'subtask'
  | 'checklist'
  | 'form'
  | 'design'
  | 'upload'
  | 'link'
  | 'stock'
  | 'member'

export type WorkspaceItemAncestor = { id: string; kind: WorkspaceItemKind }

export type WorkspaceItemReference = {
  id: string
  label: string
  kind: WorkspaceItemKind
  parentId: string | null
  /** Root-to-parent chain; empty for goal-level picks. */
  ancestors: WorkspaceItemAncestor[]
}

export type WorkspaceItemPlanMode = 'EDIT' | 'CREATE'

export type PickerCategoryKind =
  | 'forms'
  | 'designs'
  | 'uploads'
  | 'links'
  | 'stock'
  | 'timeline'
  | 'members'
  | 'chats'
  | 'new_file'
  | 'gsheet'
  | 'image'

type WorkspaceItemPickerBaseProps = {
  /** Optional open state for modal wrapper convenience */
  open?: boolean
  title?: string
  /** Mobile callers can opt into the shared bottom-sheet host without changing desktop callers. */
  mobileHost?: 'sheet' | 'inline'

  /** Alternative timeline data sources */
  elements?: TimelineElement[]
  goals?: Goal[]
  items?: Array<{ id: string; label: string; kind: WorkspaceItemKind; parentId?: string | null }>

  /** Entity lists for provisions */
  forms?: Array<{ id: string; title: string; lifecycleStatus?: string }>
  designs?: Array<{ id: string; title: string; mode?: string; boundIntent?: string }>
  uploads?: Array<{ id: string; name?: string; title?: string; sizeBytes?: number; mimeType?: string }>
  links?: Array<{ id: string; title: string; url?: string }>
  stock?: StockItem[]
  members?: Array<{ id: string; displayName: string; email?: string; role?: string }>

  /** Item attachment target */
  target?: ItemAttachmentTarget
  workspaceId?: string
  userId?: string
  canEdit?: boolean

  /** Category controls */
  enabledKinds?: PickerCategoryKind[]
  initialKind?: PickerCategoryKind

  /** Selections and handlers */
  onSelect?: (reference: WorkspaceItemReference) => void
  onClose?: () => void
  onAttach?: (kind: PickerCategoryKind, id: string) => Promise<void>
  onDetach?: (kind: PickerCategoryKind, id: string) => Promise<void>
  onDelete?: (kind: PickerCategoryKind, id: string, name: string) => Promise<void>

  selectedIds?: Set<string>
  disabledIds?: Set<string>
  activeGoalId?: string
  showHead?: boolean
  ariaLabel?: string

  initialContexts?: DataContext[]
  onCommit?: (contexts: DataContext[]) => void
  onFiles?: (files: File[]) => void
  categories?: PickerCategoryKind[]
}

type ItemAttachmentSingleSelectProps = {
  /** Mode: default is itemAttachment. */
  mode?: 'itemAttachment'
  multiSelect?: false | undefined
  onCommitRefs?: never
  initialSelectedIds?: never
  maxSelect?: never
}

type ItemAttachmentMultiSelectProps = {
  mode?: 'itemAttachment'
  multiSelect: true
  onCommitRefs: (refs: WorkspaceItemReference[]) => void
  initialSelectedIds?: string[]
  maxSelect?: number
}

type AiContextProps = {
  /** aiContext retains its independent DataContext basket. */
  mode: 'aiContext'
  multiSelect?: never
  onCommitRefs?: never
  initialSelectedIds?: never
  maxSelect?: never
}

export type WorkspaceItemPickerProps = WorkspaceItemPickerBaseProps & (
  | ItemAttachmentSingleSelectProps
  | ItemAttachmentMultiSelectProps
  | AiContextProps
)

interface TreeNode {
  id: string
  label: string
  kind: WorkspaceItemKind
  parentId: string | null
  ancestors: WorkspaceItemAncestor[]
  children: TreeNode[]
}

const KIND_LABEL: Record<WorkspaceItemKind, string> = {
  goal: 'Goal',
  task: 'Task',
  subtask: 'SubTask',
  checklist: 'Checklist',
  form: 'Form',
  design: 'Design',
  upload: 'Upload',
  link: 'Link',
  stock: 'Stock',
  member: 'Member',
}

const formatBytes = (bytes?: number) => {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const ACCEPTED_FILE_TYPES =
  'image/*,application/pdf,.xlsx,.xls,.csv,.tsv,application/vnd.openxmlformats-officedocument.*,.doc,.docx,.ppt,.pptx,.txt,.md'

const buildTreeFromElements = (elements: TimelineElement[]): TreeNode[] => {
  const byParent = new Map<string | null, TimelineElement[]>()
  for (const element of elements) {
    const key = element.parentId ?? null
    if (!byParent.has(key)) byParent.set(key, [])
    byParent.get(key)!.push(element)
  }

  const checklistsFor = (element: TimelineElement, ancestors: WorkspaceItemAncestor[]): TreeNode[] =>
    (element.checklist ?? []).map((item) => ({
      id: item.id,
      label: item.text || 'Untitled checklist',
      kind: 'checklist' as const,
      parentId: element.id,
      ancestors,
      children: [],
    }))

  const toNode = (element: TimelineElement, ancestors: WorkspaceItemAncestor[]): TreeNode => {
    const kind: WorkspaceItemKind =
      element.type === 'GOAL' ? 'goal' : element.type === 'TASK' ? 'task' : 'subtask'
    const self: WorkspaceItemAncestor = { id: element.id, kind }
    const childAncestors = [...ancestors, self]
    return {
      id: element.id,
      label: element.title,
      kind,
      parentId: element.parentId ?? null,
      ancestors,
      children: [
        ...(byParent.get(element.id) ?? []).map((child) => toNode(child, childAncestors)),
        ...checklistsFor(element, childAncestors),
      ],
    }
  }

  return (byParent.get(null) ?? [])
    .filter((element) => element.type === 'GOAL')
    .map((element) => toNode(element, []))
}

const buildTreeFromGoals = (goals: Goal[]): TreeNode[] => {
  return goals.map((goal) => {
    const goalAncestor: WorkspaceItemAncestor = { id: goal.id, kind: 'goal' }
    const goalTasks: TreeNode[] = goal.tasks.map((task) => {
      const taskAncestor: WorkspaceItemAncestor = { id: task.id, kind: 'task' }
      const taskChecklists: TreeNode[] = (task.checklist ?? []).map((item) => ({
        id: item.id,
        label: item.text || 'Untitled checklist',
        kind: 'checklist' as const,
        parentId: task.id,
        ancestors: [goalAncestor, taskAncestor],
        children: [],
      }))
      const taskSubTasks: TreeNode[] = task.subTasks.map((subTask) => {
        const subAncestor: WorkspaceItemAncestor = { id: subTask.id, kind: 'subtask' }
        const subChecklists: TreeNode[] = (subTask.checklist ?? []).map((item) => ({
          id: item.id,
          label: item.text || 'Untitled checklist',
          kind: 'checklist' as const,
          parentId: subTask.id,
          ancestors: [goalAncestor, taskAncestor, subAncestor],
          children: [],
        }))
        return {
          id: subTask.id,
          label: subTask.title,
          kind: 'subtask' as const,
          parentId: task.id,
          ancestors: [goalAncestor, taskAncestor],
          children: subChecklists,
        }
      })
      return {
        id: task.id,
        label: task.title,
        kind: 'task' as const,
        parentId: goal.id,
        ancestors: [goalAncestor],
        children: [...taskChecklists, ...taskSubTasks],
      }
    })
    return {
      id: goal.id,
      label: goal.title,
      kind: 'goal' as const,
      parentId: null,
      ancestors: [],
      children: goalTasks,
    }
  })
}

const filterTree = (nodes: TreeNode[], query: string): TreeNode[] => {
  if (!query.trim()) return nodes
  const needle = query.trim().toLowerCase()
  const visit = (node: TreeNode): TreeNode | null => {
    const matchesSelf = node.label.toLowerCase().includes(needle)
    const matchedChildren = node.children
      .map(visit)
      .filter((value): value is TreeNode => value !== null)
    if (matchesSelf || matchedChildren.length > 0) {
      return { ...node, children: matchedChildren }
    }
    return null
  }
  return nodes.map(visit).filter((value): value is TreeNode => value !== null)
}

const PickerTreeRow = ({
  node,
  depth,
  onPick,
  disabledIds,
  selectedIds,
  activeGoalRootId,
  currentGoalRootId,
  forceExpanded,
}: {
  node: TreeNode
  depth: number
  onPick: (ref: WorkspaceItemReference) => void
  disabledIds?: Set<string>
  selectedIds?: Set<string>
  activeGoalRootId?: string
  currentGoalRootId: string
  forceExpanded: boolean
}) => {
  const explicitlyDisabled = disabledIds?.has(node.id) ?? false
  const lockedOutOfGoal = Boolean(activeGoalRootId) && currentGoalRootId !== activeGoalRootId
  const disabled = explicitlyDisabled || lockedOutOfGoal
  const isSelected = selectedIds?.has(node.id) ?? false
  const hasChildren = node.children.length > 0
  const [manuallyExpanded, setManuallyExpanded] = useState(false)
  const expanded = forceExpanded || manuallyExpanded
  const reference: WorkspaceItemReference = {
    id: node.id, label: node.label, kind: node.kind, parentId: node.parentId, ancestors: node.ancestors,
  }

  return (
    <div className="wsipRow" style={{ paddingLeft: `${depth * 16}px` }}>
      <div className="wsipRowHead">
        {hasChildren ? (
          <Pressable
            className="wsipChevron"
            onClick={() => setManuallyExpanded((current) => !current)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ExpandMoreRoundedIcon fontSize="small" /> : <ChevronRightRoundedIcon fontSize="small" />}
          </Pressable>
        ) : (
          <span className="wsipChevronSpacer" aria-hidden="true" />
        )}
        <Pressable
          className={`wsipItem wsipItem--${node.kind}${disabled ? ' isDisabled' : ''}${isSelected ? ' isSelected' : ''}`}
          disabled={disabled}
          onClick={() => onPick(reference)}
        >
          <span className="wsipItemText">
            <MiddleEllipsis text={node.label} />
          </span>
          <span className="wsipItemKind">{KIND_LABEL[node.kind]}</span>
          {isSelected ? <CheckRoundedIcon fontSize="small" className="wsipItemCheck" /> : null}
        </Pressable>
      </div>
      {expanded && hasChildren && (
        <div className="wsipChildren">
          {node.children.map((child) => (
            <PickerTreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              onPick={onPick}
              disabledIds={disabledIds}
              selectedIds={selectedIds}
              activeGoalRootId={activeGoalRootId}
              currentGoalRootId={currentGoalRootId}
              forceExpanded={forceExpanded}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const CATEGORY_ICONS: Record<PickerCategoryKind, ComponentType<{ className?: string; fontSize?: 'small' | 'inherit' }>> = {
  forms: AssignmentRoundedIcon,
  designs: BrushRoundedIcon,
  uploads: FolderOpenRoundedIcon,
  links: LinkRoundedIcon,
  stock: Inventory2RoundedIcon,
  timeline: TimelineRoundedIcon,
  members: GroupRoundedIcon,
  chats: ForumRoundedIcon,
  new_file: NoteAddRoundedIcon,
  gsheet: GridOnRoundedIcon,
  image: ImageRoundedIcon,
}

const CATEGORY_LABELS: Record<PickerCategoryKind, string> = {
  forms: 'Forms',
  designs: 'Designs',
  uploads: 'Uploads',
  links: 'Links',
  stock: 'Stock',
  timeline: 'Timeline items',
  members: 'Members',
  chats: 'Chats',
  new_file: 'New file',
  gsheet: 'Google Sheet',
  image: 'Images',
}

const EMPTY_CONTEXTS: DataContext[] = []

export const WorkspaceItemPicker = ({
  open,
  title,
  mobileHost = 'inline',
  elements,
  goals,
  items,
  forms,
  designs,
  uploads,
  links,
  stock,
  members,
  target,
  workspaceId: explicitWorkspaceId,
  userId: explicitUserId,
  canEdit = true,
  enabledKinds: explicitEnabledKinds,
  initialKind,
  onSelect,
  onClose,
  onAttach,
  onDetach,
  onDelete,
  selectedIds,
  disabledIds,
  activeGoalId,
  showHead = true,
  ariaLabel,
  mode = 'itemAttachment',
  multiSelect,
  onCommitRefs,
  initialSelectedIds,
  maxSelect,
  initialContexts = EMPTY_CONTEXTS,
  onCommit,
  onFiles,
  categories,
}: WorkspaceItemPickerProps) => {
  const { currentUser } = useAuth()
  const { activeWorkspaceId, activeRole, workspaces } = useWorkspace()
  const { showToast } = useToast()
  const { t } = useNls()

  const workspaceId = explicitWorkspaceId || target?.workspaceId || activeWorkspaceId || ''
  const userId = explicitUserId || currentUser?.uid || ''
  const activeWorkspace = workspaces.find((w) => w.id === workspaceId)
  const isAdmin = roleCanPerform(activeRole, 'ADMIN')

  // Determine available kinds
  const availableKinds = useMemo<PickerCategoryKind[]>(() => {
    if (explicitEnabledKinds && explicitEnabledKinds.length > 0) {
      return explicitEnabledKinds
    }
    if (categories && categories.length > 0) {
      return categories
    }
    if (mode === 'aiContext') {
      return ['uploads', 'forms', 'designs', 'chats', 'new_file', 'gsheet', 'image', 'links']
    }
    const detected: PickerCategoryKind[] = []
    if (forms) detected.push('forms')
    if (designs) detected.push('designs')
    if (uploads) detected.push('uploads')
    if (links) detected.push('links')
    if (stock) detected.push('stock')
    if (goals || elements || items) detected.push('timeline')
    return detected.length > 0 ? detected : ['forms', 'designs', 'uploads', 'links']
  }, [explicitEnabledKinds, categories, mode, forms, designs, uploads, links, stock, goals, elements, items])

  const [selectedKind, setSelectedKind] = useState<PickerCategoryKind>(
    initialKind && availableKinds.includes(initialKind) ? initialKind : availableKinds[0] || 'forms',
  )
  const activeKind: PickerCategoryKind = availableKinds.includes(selectedKind)
    ? selectedKind
    : availableKinds[0] || 'forms'

  const [searchQuery, setSearchQuery] = useState('')
  const [fileTypeFilter, setFileTypeFilter] = useState<'ALL' | 'IMAGE' | 'DOCUMENT' | 'SPREADSHEET' | 'MEDIA'>('ALL')
  const [stockLocationFilter, setStockLocationFilter] = useState<string>('ALL')
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [openRowMenuId, setOpenRowMenuId] = useState<string | null>(null)
  const [itemPendingDelete, setItemPendingDelete] = useState<{ kind: PickerCategoryKind; id: string; name: string } | null>(null)
  const [pendingLeaveHub, setPendingLeaveHub] = useState<{ hubKey: HubKey; hubName: string; path: string } | null>(null)

  // AI Context state
  const [basket, setBasket] = useState<DataContext[]>(() => initialContexts ?? EMPTY_CONTEXTS)
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setBasket(initialContexts ?? EMPTY_CONTEXTS)
    }
  }
  const [subscribedFiles, setSubscribedFiles] = useState<WorkspaceFileMetadata[]>([])
  const [subscribedElements, setSubscribedElements] = useState<TimelineElement[]>([])

  useEffect(() => {
    if (elements || !workspaceId) return
    if (!explicitEnabledKinds?.includes('timeline') && activeKind !== 'timeline') return

    const unsub = subscribeTimelineElements(workspaceId, (list) => {
      setSubscribedElements(list)
    })
    return () => unsub()
  }, [elements, workspaceId, explicitEnabledKinds, activeKind])

  useEffect(() => {
    if (mode === 'aiContext' && workspaceId && userId) {
      const unsub = subscribeWorkspaceFiles(workspaceId, userId, false, setSubscribedFiles)
      return () => {
        if (typeof unsub === 'function') unsub()
      }
    }
  }, [mode, workspaceId, userId])

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Combined attached sets
  const attachedFormSet = useMemo(() => new Set(target?.attachedFormIds ?? []), [target?.attachedFormIds])
  const attachedDesignSet = useMemo(() => new Set(target?.attachedDesignIds ?? []), [target?.attachedDesignIds])
  const attachedUploadSet = useMemo(() => new Set(target?.attachedUploadIds ?? []), [target?.attachedUploadIds])
  const attachedLinkSet = useMemo(() => new Set(target?.attachedLinkIds ?? []), [target?.attachedLinkIds])

  const totalAttachedCount = useMemo(() => {
    if (selectedIds && selectedIds.size > 0) return selectedIds.size
    return (
      (target?.attachedFormIds?.length ?? 0) +
      (target?.attachedDesignIds?.length ?? 0) +
      (target?.attachedUploadIds?.length ?? 0) +
      (target?.attachedLinkIds?.length ?? 0)
    )
  }, [selectedIds, target?.attachedFormIds, target?.attachedDesignIds, target?.attachedUploadIds, target?.attachedLinkIds])

  const isItemAttached = (kind: PickerCategoryKind, id: string): boolean => {
    if (selectedIds?.has(id)) return true
    if (kind === 'forms') return attachedFormSet.has(id)
    if (kind === 'designs') return attachedDesignSet.has(id)
    if (kind === 'uploads') return attachedUploadSet.has(id)
    if (kind === 'links') return attachedLinkSet.has(id)
    return false
  }

  // Cross-hub jump request
  const requestLeaveHub = (hubKey: HubKey, hubName: string, path: string) => {
    if (isLeaveForHubConfirmed(hubKey)) {
      window.history.pushState(null, '', toConsolePath(path))
      window.dispatchEvent(new PopStateEvent('popstate'))
      onClose?.()
    } else {
      setPendingLeaveHub({ hubKey, hubName, path })
    }
  }

  // Row selection / attachment
  const handleSelectRow = async (ref: WorkspaceItemReference) => {
    if (onSelect) {
      onSelect(ref)
      return
    }
    const catKind = ref.kind === 'form' ? 'forms' : ref.kind === 'design' ? 'designs' : ref.kind === 'upload' ? 'uploads' : ref.kind === 'link' ? 'links' : ref.kind === 'stock' ? 'stock' : 'timeline'
    if (onAttach) {
      await onAttach(catKind, ref.id)
    }
  }

  // Detach row action
  const handleDetach = async (kind: PickerCategoryKind, id: string) => {
    setOpenRowMenuId(null)
    if (onDetach) {
      await onDetach(kind, id)
      showToast('Item detached.', 'success')
      return
    }
    if (target) {
      await detachProvisionFromItem(target, kind, id)
      showToast('Item detached.', 'success')
    }
  }

  // Delete row action
  const handleDeleteRequest = (kind: PickerCategoryKind, id: string, name: string) => {
    setOpenRowMenuId(null)
    setItemPendingDelete({ kind, id, name })
  }

  const handleConfirmDelete = async () => {
    if (!itemPendingDelete) return
    const { kind, id, name } = itemPendingDelete
    setItemPendingDelete(null)
    try {
      if (onDelete) {
        await onDelete(kind, id, name)
      } else {
        await deleteWorkspaceProvisionPermanently({
          workspaceId,
          kind,
          id,
          target,
          actor: { id: currentUser?.uid || '', name: currentUser?.displayName || 'User' },
        })
      }
      showToast(`${name} deleted permanently.`, 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Delete failed.', 'error')
    }
  }

  // File upload handler
  const handleUploadFiles = async (filesToUpload: File[]) => {
    if (filesToUpload.length === 0) return
    if (!workspaceId) {
      showToast('No active workspace selected for upload.', 'error')
      return
    }
    setIsUploading(true)
    try {
      if (onFiles && mode === 'aiContext') {
        onFiles(filesToUpload)
        setIsUploading(false)
        return
      }

      const results = await uploadWorkspaceFiles({
        workspaceId,
        files: filesToUpload,
        visibility: 'WORKSPACE_SHARED',
        source: 'ITEM_ATTACHMENT',
      })

      for (const res of results) {
        if (res.fileId) {
          if (target) {
            await attachUploadToItem(target, res.fileId)
          } else if (onAttach) {
            await onAttach('uploads', res.fileId)
          }
        }
      }
      showToast(results.length === 1 ? 'File uploaded and attached.' : `${results.length} files attached.`, 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Upload failed.', 'error')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const effectiveElements = elements ?? (subscribedElements.length > 0 ? subscribedElements : undefined)

  // Timeline tree construction
  const timelineTree = useMemo(() => {
    if (items) {
      return items.map((item) => ({
        id: item.id,
        label: item.label,
        kind: item.kind,
        parentId: item.parentId ?? null,
        ancestors: [],
        children: [],
      }))
    }
    if (goals) return buildTreeFromGoals(goals)
    if (effectiveElements) return buildTreeFromElements(effectiveElements)
    return []
  }, [effectiveElements, goals, items])

  const filteredTree = useMemo(() => filterTree(timelineTree, searchQuery), [timelineTree, searchQuery])

  // Filtered lists for provisions
  const filteredForms = useMemo(() => {
    const list = forms ?? []
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase().trim()
    return list.filter((f) => f.title.toLowerCase().includes(q))
  }, [forms, searchQuery])

  const filteredDesigns = useMemo(() => {
    const list = designs ?? []
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase().trim()
    return list.filter((d) => d.title.toLowerCase().includes(q) || (d.mode || '').toLowerCase().includes(q))
  }, [designs, searchQuery])

  const effectiveUploads = useMemo(() => {
    if (uploads) return uploads
    if (mode === 'aiContext') {
      return subscribedFiles.map((f) => ({ ...f, name: f.fileName, title: f.fileName }))
    }
    return []
  }, [uploads, mode, subscribedFiles])
  const filteredUploads = useMemo(() => {
    return effectiveUploads.filter((file) => {
      const name = (file.name || file.title || '').toLowerCase()
      if (searchQuery.trim() && !name.includes(searchQuery.toLowerCase().trim())) return false
      if (fileTypeFilter === 'IMAGE') return file.mimeType?.startsWith('image/')
      if (fileTypeFilter === 'DOCUMENT') {
        return (
          file.mimeType?.includes('pdf') ||
          file.mimeType?.includes('word') ||
          file.mimeType?.includes('text')
        )
      }
      if (fileTypeFilter === 'SPREADSHEET') {
        return (
          file.mimeType?.includes('spreadsheet') ||
          file.mimeType?.includes('excel') ||
          file.mimeType?.includes('csv')
        )
      }
      if (fileTypeFilter === 'MEDIA') {
        return file.mimeType?.startsWith('audio/') || file.mimeType?.startsWith('video/')
      }
      return true
    })
  }, [effectiveUploads, searchQuery, fileTypeFilter])

  const filteredLinks = useMemo(() => {
    const list = links ?? []
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase().trim()
    return list.filter((l) => l.title.toLowerCase().includes(q) || (l.url || '').toLowerCase().includes(q))
  }, [links, searchQuery])

  const stockLocations = useMemo(() => {
    const set = new Set<string>()
    for (const item of stock ?? []) {
      if (item.location) set.add(item.location)
    }
    return Array.from(set)
  }, [stock])

  const filteredStock = useMemo(() => {
    const list = stock ?? []
    return list.filter((item) => {
      if (stockLocationFilter !== 'ALL' && item.location !== stockLocationFilter) return false
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim()
        return item.name.toLowerCase().includes(q) || (item.location || '').toLowerCase().includes(q)
      }
      return true
    })
  }, [stock, searchQuery, stockLocationFilter])

  const flatReferences = useMemo<WorkspaceItemReference[]>(() => [
    ...(forms ?? []).map((form) => ({ id: form.id, label: form.title, kind: 'form' as const, parentId: null, ancestors: [] })),
    ...(designs ?? []).map((design) => ({ id: design.id, label: design.title, kind: 'design' as const, parentId: null, ancestors: [] })),
    ...effectiveUploads.map((file) => ({ id: file.id, label: file.name || file.title || 'Untitled upload', kind: 'upload' as const, parentId: null, ancestors: [] })),
    ...(links ?? []).map((link) => ({ id: link.id, label: link.title, kind: 'link' as const, parentId: null, ancestors: [] })),
    ...(stock ?? []).map((item) => ({ id: item.id, label: item.name, kind: 'stock' as const, parentId: null, ancestors: [] })),
    ...(members ?? []).map((member) => ({ id: member.id, label: member.displayName, kind: 'member' as const, parentId: null, ancestors: [] })),
  ], [designs, effectiveUploads, forms, links, members, stock])
  const [selectedRefs, setSelectedRefs] = useState<WorkspaceItemReference[]>([])
  const selectedRefIds = useMemo(() => new Set(selectedRefs.map((ref) => ref.id)), [selectedRefs])
  const selectedRefsOpenRef = useRef(false)
  const isRefMultiSelect = mode === 'itemAttachment' && multiSelect === true

  useEffect(() => {
    if (!isRefMultiSelect || open === false) {
      selectedRefsOpenRef.current = false
      return
    }
    if (selectedRefsOpenRef.current) return
    const initialIds = new Set(initialSelectedIds)
    const seeded = flatReferences.filter((ref) => initialIds.has(ref.id))
    setSelectedRefs(maxSelect === undefined ? seeded : seeded.slice(0, Math.max(0, maxSelect)))
    selectedRefsOpenRef.current = true
  }, [flatReferences, initialSelectedIds, isRefMultiSelect, maxSelect, open])

  const toggleSelectedRef = (ref: WorkspaceItemReference) => {
    setSelectedRefs((current) => {
      if (current.some((selected) => selected.id === ref.id)) {
        return current.filter((selected) => selected.id !== ref.id)
      }
      if (maxSelect !== undefined && current.length >= maxSelect) return current
      return [...current, ref]
    })
  }

  const handleFlatRowClick = (ref: WorkspaceItemReference) => {
    if (isRefMultiSelect) {
      toggleSelectedRef(ref)
      return
    }
    void handleSelectRow(ref)
  }

  const isFlatSelectionDisabled = (id: string) => Boolean(
    isRefMultiSelect
    && maxSelect !== undefined
    && selectedRefs.length >= maxSelect
    && !selectedRefIds.has(id),
  )

  // AI basket helpers
  const basketIdSet = useMemo(() => new Set(basket.map((item) => item.id)), [basket])
  const toggleContext = (context: DataContext) => {
    setBasket((current) =>
      current.some((item) => item.id === context.id)
        ? current.filter((item) => item.id !== context.id)
        : [...current, context],
    )
  }
  const addContext = (context: DataContext) => {
    setBasket((current) =>
      current.some((item) => item.id === context.id)
        ? current.map((item) => (item.id === context.id ? context : item))
        : [...current, context],
    )
  }
  const costEstimate = useMemo(() => estimateContextCost(basket, 'GENERATION'), [basket])

  const renderContent = () => {
    // If used inline for timeline tree without multi-kind
    if (availableKinds.length === 1 && availableKinds[0] === 'timeline' && !open) {
      return (
        <div className="wsipRoot" role="dialog" aria-label={ariaLabel ?? 'Pick a workspace item'}>
          {showHead && (
            <div className="wsipHead">
              <IconButton
                variant="tertiary"
                size="lg"
                label="Back"
                icon={<ArrowBackRoundedIcon fontSize="small" />}
                onClick={onClose}
              />
              <div className="wsipSearch">
                <SearchRoundedIcon fontSize="small" aria-hidden="true" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search items…"
                  aria-label="Search workspace items"
                />
              </div>
            </div>
          )}
          <div className="wsipBody">
            {filteredTree.length === 0 ? (
              <p className="wsipEmpty">No items match.</p>
            ) : (
              filteredTree.map((node) => (
                <PickerTreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  onPick={handleSelectRow}
                  disabledIds={disabledIds}
                  selectedIds={selectedIds}
                  activeGoalRootId={activeGoalId}
                  currentGoalRootId={node.id}
                  forceExpanded={searchQuery.trim().length > 0}
                />
              ))
            )}
          </div>
        </div>
      )
    }

    const showLeftRail = availableKinds.length > 1

    return (
      <div className="wsipRoot wsipRoot--dialog" aria-label={ariaLabel ?? title ?? 'Attach provisions'} data-tour-id="provision-picker">
        {/* Header */}
        <header className="wsipModalHeader">
          <h2 className="wsipModalTitle">
            {title ?? (mode === 'aiContext' ? t('NLSAiContextPickerTitle') : 'Attach provisions')}
          </h2>
          {onClose && (
            <IconButton
              variant="tertiary"
              size="sm"
              label="Close picker"
              icon={<CloseRoundedIcon fontSize="small" />}
              onClick={onClose}
            />
          )}
        </header>

        {/* 2-Pane Body */}
        <div className="wsipBodyContainer">
          {/* Left Rail */}
          {showLeftRail && (
            <nav className="wsipRail" aria-label="Provision categories">
              {availableKinds.map((kind) => {
                const Icon = CATEGORY_ICONS[kind]
                const isActive = activeKind === kind
                return (
                  <Pressable
                    key={kind}
                    className={`wsipRailTab${isActive ? ' isActive' : ''}`}
                    onClick={() => {
                      setSelectedKind(kind)
                      setSearchQuery('')
                    }}
                    aria-selected={isActive}
                  >
                    <Icon fontSize="small" />
                    <span>{CATEGORY_LABELS[kind]}</span>
                  </Pressable>
                )
              })}
            </nav>
          )}

          {/* Right Pane */}
          <main
            className={`wsipContentPane${isDragging ? ' isDragging' : ''}`}
            onDragOver={(e) => {
              if (activeKind === 'uploads') {
                e.preventDefault()
                setIsDragging(true)
              }
            }}
            onDragLeave={(e) => {
              if (activeKind === 'uploads' && !e.currentTarget.contains(e.relatedTarget as Node)) {
                setIsDragging(false)
              }
            }}
            onDrop={(e) => {
              if (activeKind === 'uploads') {
                e.preventDefault()
                setIsDragging(false)
                const droppedFiles = Array.from(e.dataTransfer.files)
                void handleUploadFiles(droppedFiles)
              }
            }}
          >
            {/* Sticky Top Bar */}
            {!(mode === 'aiContext' && activeKind !== 'uploads') && (
              <div className="wsipStickyBar">
                <div className="wsipSearch">
                  <SearchRoundedIcon fontSize="small" style={{ color: 'var(--muted)' }} />
                  <input
                    type="search"
                    placeholder={`Search ${CATEGORY_LABELS[activeKind].toLowerCase()}…`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label={`Search ${CATEGORY_LABELS[activeKind]}`}
                  />
                </div>

                {/* Kind-specific controls */}
                {activeKind === 'forms' && (
                  <span className="wsipNoticeCaption">Only active forms shown</span>
                )}

                {activeKind === 'uploads' && (
                  <div className="wsipUploadsToolbar">
                    <div className="wsipFilterChips">
                      {(['ALL', 'IMAGE', 'DOCUMENT', 'SPREADSHEET', 'MEDIA'] as const).map((filter) => (
                        <Button
                          key={filter}
                          variant="tertiary"
                          size="sm"
                          pressed={fileTypeFilter === filter}
                          onClick={() => setFileTypeFilter(filter)}
                        >
                          {filter}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<UploadFileRoundedIcon fontSize="small" />}
                      disabled={isUploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {isUploading ? 'Uploading…' : 'Upload files'}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ACCEPTED_FILE_TYPES}
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const picked = Array.from(e.target.files ?? [])
                        void handleUploadFiles(picked)
                      }}
                    />
                  </div>
                )}

                {activeKind === 'stock' && stockLocations.length > 0 && (
                  <div className="wsipFilterChips">
                    <Button
                      variant="tertiary"
                      size="sm"
                      pressed={stockLocationFilter === 'ALL'}
                      onClick={() => setStockLocationFilter('ALL')}
                    >
                      All locations
                    </Button>
                    {stockLocations.map((loc) => (
                      <Button
                        key={loc}
                        variant="tertiary"
                        size="sm"
                        pressed={stockLocationFilter === loc}
                        onClick={() => setStockLocationFilter(loc)}
                      >
                        {loc}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Upload Spinner Bar */}
            {isUploading && (
              <div className="wsipUploadingBanner">
                <MiniLoader size="xs" inline />
                <span>Uploading and attaching file…</span>
              </div>
            )}

            {/* List / Content Area */}
            <div className="wsipListScrollRegion">
              {activeKind === 'forms' ? (
                mode === 'aiContext' ? (
                  <FormsResponsesPane
                    workspaceId={workspaceId}
                    basket={basket}
                    onToggle={toggleContext}
                    mode={mode}
                    onAttachForm={onAttach ? (id) => onAttach('forms', id) : undefined}
                    onDetachForm={onDetach ? (id) => onDetach('forms', id) : undefined}
                    attachedFormIds={attachedFormSet}
                  />
                ) : filteredForms.length === 0 ? (
                  <div className="wsipEmptyState">
                    <AssignmentRoundedIcon style={{ fontSize: 36, opacity: 0.35 }} />
                    <p className="wsipEmptyStateTitle">No active forms yet.</p>
                    <p className="wsipEmptyStateDesc">Create an active form in Forms Hub to attach here.</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      trailingIcon={<OpenInNewRoundedIcon fontSize="small" />}
                      onClick={() =>
                        requestLeaveHub(
                          'forms',
                          'Forms Hub',
                          activeWorkspace ? `/workspace/${activeWorkspace.slug}/forms` : '/forms',
                        )
                      }
                    >
                      Create a form
                    </Button>
                  </div>
                ) : (
                  filteredForms.map((form) => {
                    const attached = isItemAttached('forms', form.id)
                    const ref: WorkspaceItemReference = { id: form.id, label: form.title, kind: 'form', parentId: null, ancestors: [] }
                    const selected = selectedRefIds.has(form.id)
                    const selectionDisabled = isFlatSelectionDisabled(form.id)
                    return (
                      <div key={form.id} className={`wsipRowItem${attached ? ' isAttached' : ''}`}>
                        <Pressable
                          className={`wsipRowMain${isRefMultiSelect ? ' wsipRow--multi' : ''}${selected ? ' isSelected' : ''}`}
                          title={form.title}
                          disabled={attached || selectionDisabled}
                          onClick={() => handleFlatRowClick(ref)}
                        >
                          <span className="wsipRowIcon">
                            <AssignmentRoundedIcon fontSize="small" />
                          </span>
                          <div className="wsipRowContent">
                            <span className="wsipRowTitle">
                              <MiddleEllipsis text={form.title} />
                            </span>
                            <span className="wsipRowSecondary">Active form</span>
                          </div>
                        </Pressable>
                        <div className="wsipRowTrailing">
                          {isRefMultiSelect && <span className="wsipRow__checkbox" aria-hidden="true">{selected && <CheckRoundedIcon fontSize="inherit" />}</span>}
                          {attached && <span className="wsipAttachedBadge">Attached</span>}
                          {attached && (
                            <div className="wsipActionMenuWrap">
                              <IconButton
                                variant="tertiary"
                                size="sm"
                                label={`Actions for ${form.title}`}
                                icon={<MoreHorizRoundedIcon fontSize="small" />}
                                onClick={() => setOpenRowMenuId(openRowMenuId === form.id ? null : form.id)}
                              />
                              {openRowMenuId === form.id && (
                                <div className="wsipDropdownMenu">
                                  {canEdit && (
                                    <Pressable
                                      className="wsipDropdownItem"
                                      onClick={() => void handleDetach('forms', form.id)}
                                    >
                                      <LinkOffRoundedIcon fontSize="small" />
                                      <span>Detach from this item</span>
                                    </Pressable>
                                  )}
                                  {isAdmin && (
                                    <Pressable
                                      className="wsipDropdownItem isDanger"
                                      onClick={() => handleDeleteRequest('forms', form.id, form.title)}
                                    >
                                      <DeleteOutlineRoundedIcon fontSize="small" />
                                      <span>Delete permanently</span>
                                    </Pressable>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })
                )
              ) : activeKind === 'designs' ? (
                mode === 'aiContext' ? (
                  <DesignsPane
                    workspaceId={workspaceId}
                    basket={basket}
                    onToggle={toggleContext}
                    mode={mode}
                    onAttachDesign={onAttach ? (id) => onAttach('designs', id) : undefined}
                    onDetachDesign={onDetach ? (id) => onDetach('designs', id) : undefined}
                    attachedDesignIds={attachedDesignSet}
                  />
                ) : filteredDesigns.length === 0 ? (
                  <div className="wsipEmptyState">
                    <BrushRoundedIcon style={{ fontSize: 36, opacity: 0.35 }} />
                    <p className="wsipEmptyStateTitle">No completed designs yet.</p>
                    <p className="wsipEmptyStateDesc">Create and finalize a design in Design Hub to attach here.</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      trailingIcon={<OpenInNewRoundedIcon fontSize="small" />}
                      onClick={() =>
                        requestLeaveHub(
                          'designs',
                          'Design Hub',
                          activeWorkspace ? `/workspace/${activeWorkspace.slug}/design` : '/design',
                        )
                      }
                    >
                      Create a design
                    </Button>
                  </div>
                ) : (
                  filteredDesigns.map((design) => {
                    const attached = isItemAttached('designs', design.id)
                    const ref: WorkspaceItemReference = { id: design.id, label: design.title, kind: 'design', parentId: null, ancestors: [] }
                    const selected = selectedRefIds.has(design.id)
                    const selectionDisabled = isFlatSelectionDisabled(design.id)
                    return (
                      <div key={design.id} className={`wsipRowItem${attached ? ' isAttached' : ''}`}>
                        <Pressable
                          className={`wsipRowMain${isRefMultiSelect ? ' wsipRow--multi' : ''}${selected ? ' isSelected' : ''}`}
                          title={design.title}
                          disabled={attached || selectionDisabled}
                          onClick={() => handleFlatRowClick(ref)}
                        >
                          <span className="wsipRowIcon">
                            <BrushRoundedIcon fontSize="small" />
                          </span>
                          <div className="wsipRowContent">
                            <span className="wsipRowTitle">
                              <MiddleEllipsis text={design.title} />
                            </span>
                            <span className="wsipRowSecondary">
                              {design.mode ? `Design · ${design.mode}` : 'Design asset'}
                            </span>
                          </div>
                        </Pressable>
                        <div className="wsipRowTrailing">
                          {isRefMultiSelect && <span className="wsipRow__checkbox" aria-hidden="true">{selected && <CheckRoundedIcon fontSize="inherit" />}</span>}
                          {attached && <span className="wsipAttachedBadge">Attached</span>}
                          {attached && (
                            <div className="wsipActionMenuWrap">
                              <IconButton
                                variant="tertiary"
                                size="sm"
                                label={`Actions for ${design.title}`}
                                icon={<MoreHorizRoundedIcon fontSize="small" />}
                                onClick={() => setOpenRowMenuId(openRowMenuId === design.id ? null : design.id)}
                              />
                              {openRowMenuId === design.id && (
                                <div className="wsipDropdownMenu">
                                  {canEdit && (
                                    <Pressable
                                      className="wsipDropdownItem"
                                      onClick={() => void handleDetach('designs', design.id)}
                                    >
                                      <LinkOffRoundedIcon fontSize="small" />
                                      <span>Detach from this item</span>
                                    </Pressable>
                                  )}
                                  {isAdmin && (
                                    <Pressable
                                      className="wsipDropdownItem isDanger"
                                      onClick={() => handleDeleteRequest('designs', design.id, design.title)}
                                    >
                                      <DeleteOutlineRoundedIcon fontSize="small" />
                                      <span>Delete permanently</span>
                                    </Pressable>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })
                )
              ) : activeKind === 'uploads' ? (
                filteredUploads.length === 0 ? (
                  <div className="wsipEmptyState">
                    <FolderOpenRoundedIcon style={{ fontSize: 36, opacity: 0.35 }} />
                    <p className="wsipEmptyStateTitle">No files uploaded yet.</p>
                    <p className="wsipEmptyStateDesc">Drag and drop images, PDFs, or spreadsheets here, or click upload.</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<UploadFileRoundedIcon fontSize="small" />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload files
                    </Button>
                  </div>
                ) : (
                  filteredUploads.map((file) => {
                    const label = file.name || file.title || 'Untitled upload'
                    const attached = isItemAttached('uploads', file.id)
                    const isImage = file.mimeType?.startsWith('image/')
                    const ref: WorkspaceItemReference = { id: file.id, label, kind: 'upload', parentId: null, ancestors: [] }
                    const selected = selectedRefIds.has(file.id)
                    const selectionDisabled = isFlatSelectionDisabled(file.id)
                    return (
                      <div key={file.id} className={`wsipRowItem${attached ? ' isAttached' : ''}`}>
                        <Pressable
                          className={`wsipRowMain${isRefMultiSelect ? ' wsipRow--multi' : ''}${selected ? ' isSelected' : ''}`}
                          title={label}
                          disabled={attached || selectionDisabled}
                          onClick={() => {
                            if (mode === 'aiContext') {
                              toggleContext(workspaceFileToDataContext(file as WorkspaceFileMetadata))
                            } else {
                              handleFlatRowClick(ref)
                            }
                          }}
                        >
                          <span className="wsipRowIcon">
                            {isImage ? (
                              <ImageRoundedIcon fontSize="small" />
                            ) : (
                              <InsertDriveFileRoundedIcon fontSize="small" />
                            )}
                          </span>
                          <div className="wsipRowContent">
                            <span className="wsipRowTitle">
                              <MiddleEllipsis text={label} />
                            </span>
                            <OverflowTip label={`${formatBytes(file.sizeBytes)} · ${file.mimeType || 'file'}`}>
                              <span className="wsipRowSecondary">
                                {formatBytes(file.sizeBytes)} · {file.mimeType || 'file'}
                              </span>
                            </OverflowTip>
                          </div>
                        </Pressable>
                        <div className="wsipRowTrailing">
                          {mode === 'aiContext' ? (
                            <div className="wsipCheckboxSquare">
                              {basketIdSet.has(`asset:${file.id}`) && <CheckRoundedIcon style={{ fontSize: 13 }} />}
                            </div>
                          ) : (
                            <>
                              {isRefMultiSelect && <span className="wsipRow__checkbox" aria-hidden="true">{selected && <CheckRoundedIcon fontSize="inherit" />}</span>}
                              {attached && <span className="wsipAttachedBadge">Attached</span>}
                              {attached && (
                            <div className="wsipActionMenuWrap">
                              <IconButton
                                variant="tertiary"
                                size="sm"
                                label={`Actions for ${label}`}
                                icon={<MoreHorizRoundedIcon fontSize="small" />}
                                onClick={() => setOpenRowMenuId(openRowMenuId === file.id ? null : file.id)}
                              />
                              {openRowMenuId === file.id && (
                                <div className="wsipDropdownMenu">
                                  {canEdit && (
                                    <Pressable
                                      className="wsipDropdownItem"
                                      onClick={() => void handleDetach('uploads', file.id)}
                                    >
                                      <LinkOffRoundedIcon fontSize="small" />
                                      <span>Detach from this item</span>
                                    </Pressable>
                                  )}
                                  {isAdmin && (
                                    <Pressable
                                      className="wsipDropdownItem isDanger"
                                      onClick={() => handleDeleteRequest('uploads', file.id, label)}
                                    >
                                      <DeleteOutlineRoundedIcon fontSize="small" />
                                      <span>Delete permanently</span>
                                    </Pressable>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                            </>
                          )}
                        </div>
                      </div>
                    )
                  })
                )
              ) : activeKind === 'links' ? (
                mode === 'aiContext' ? (
                  <PublicLinkPane workspaceId={workspaceId} onAdd={addContext} />
                ) : filteredLinks.length === 0 ? (
                  <div className="wsipEmptyState">
                    <LinkRoundedIcon style={{ fontSize: 36, opacity: 0.35 }} />
                    <p className="wsipEmptyStateTitle">No links available yet.</p>
                    <p className="wsipEmptyStateDesc">Manage your external links in Link Hub.</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      trailingIcon={<OpenInNewRoundedIcon fontSize="small" />}
                      onClick={() =>
                        requestLeaveHub(
                          'links',
                          'Link Hub',
                          activeWorkspace ? `/workspace/${activeWorkspace.slug}/link` : '/link',
                        )
                      }
                    >
                      Go to Link Hub
                    </Button>
                  </div>
                ) : (
                  filteredLinks.map((link) => {
                    const attached = isItemAttached('links', link.id)
                    const ref: WorkspaceItemReference = { id: link.id, label: link.title, kind: 'link', parentId: null, ancestors: [] }
                    const selected = selectedRefIds.has(link.id)
                    const selectionDisabled = isFlatSelectionDisabled(link.id)
                    return (
                      <div key={link.id} className={`wsipRowItem${attached ? ' isAttached' : ''}`}>
                        <Pressable
                          className={`wsipRowMain${isRefMultiSelect ? ' wsipRow--multi' : ''}${selected ? ' isSelected' : ''}`}
                          title={link.title}
                          disabled={attached || selectionDisabled}
                          onClick={() => handleFlatRowClick(ref)}
                        >
                          <span className="wsipRowIcon">
                            <LinkRoundedIcon fontSize="small" />
                          </span>
                          <div className="wsipRowContent">
                            <span className="wsipRowTitle">
                              <MiddleEllipsis text={link.title} />
                            </span>
                            <OverflowTip label={link.url || 'Web link'}>
                              <span className="wsipRowSecondary">{link.url || 'Web link'}</span>
                            </OverflowTip>
                          </div>
                        </Pressable>
                        <div className="wsipRowTrailing">
                          {isRefMultiSelect && <span className="wsipRow__checkbox" aria-hidden="true">{selected && <CheckRoundedIcon fontSize="inherit" />}</span>}
                          {attached && <span className="wsipAttachedBadge">Attached</span>}
                          {attached && (
                            <div className="wsipActionMenuWrap">
                              <IconButton
                                variant="tertiary"
                                size="sm"
                                label={`Actions for ${link.title}`}
                                icon={<MoreHorizRoundedIcon fontSize="small" />}
                                onClick={() => setOpenRowMenuId(openRowMenuId === link.id ? null : link.id)}
                              />
                              {openRowMenuId === link.id && (
                                <div className="wsipDropdownMenu">
                                  {canEdit && (
                                    <Pressable
                                      className="wsipDropdownItem"
                                      onClick={() => void handleDetach('links', link.id)}
                                    >
                                      <LinkOffRoundedIcon fontSize="small" />
                                      <span>Detach from this item</span>
                                    </Pressable>
                                  )}
                                  {isAdmin && (
                                    <Pressable
                                      className="wsipDropdownItem isDanger"
                                      onClick={() => handleDeleteRequest('links', link.id, link.title)}
                                    >
                                      <DeleteOutlineRoundedIcon fontSize="small" />
                                      <span>Delete permanently</span>
                                    </Pressable>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })
                )
              ) : activeKind === 'stock' ? (
                filteredStock.length === 0 ? (
                  <div className="wsipEmptyState">
                    <Inventory2RoundedIcon style={{ fontSize: 36, opacity: 0.35 }} />
                    <p className="wsipEmptyStateTitle">No stock items available.</p>
                    <p className="wsipEmptyStateDesc">Manage inventory items in Stock Hub.</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      trailingIcon={<OpenInNewRoundedIcon fontSize="small" />}
                      onClick={() =>
                        requestLeaveHub(
                          'stock',
                          'Stock Hub',
                          activeWorkspace ? `/workspace/${activeWorkspace.slug}/stock` : '/stock',
                        )
                      }
                    >
                      Manage stock
                    </Button>
                  </div>
                ) : (
                  filteredStock.map((item) => {
                    const availableQty = Math.max(0, item.quantity - item.reservedQuantity)
                    const attached = selectedIds?.has(item.id) ?? false
                    const ref: WorkspaceItemReference = { id: item.id, label: item.name, kind: 'stock', parentId: null, ancestors: [] }
                    const selected = selectedRefIds.has(item.id)
                    const selectionDisabled = isFlatSelectionDisabled(item.id)
                    return (
                      <div key={item.id} className={`wsipRowItem${attached ? ' isAttached' : ''}`}>
                        <Pressable
                          className={`wsipRowMain${isRefMultiSelect ? ' wsipRow--multi' : ''}${selected ? ' isSelected' : ''}`}
                          title={item.name}
                          disabled={attached || selectionDisabled}
                          onClick={() => handleFlatRowClick(ref)}
                        >
                          <span className="wsipRowIcon">
                            <Inventory2RoundedIcon fontSize="small" />
                          </span>
                          <div className="wsipRowContent">
                            <span className="wsipRowTitle">
                              <MiddleEllipsis text={item.name} />
                            </span>
                            <OverflowTip label={`${item.location ? `${item.location} · ` : ''}${availableQty}/${item.quantity} ${item.unit || 'units'} available`}>
                              <span className="wsipRowSecondary">
                                {item.location ? `${item.location} · ` : ''}
                                {availableQty}/{item.quantity} {item.unit || 'units'} available
                              </span>
                            </OverflowTip>
                          </div>
                        </Pressable>
                        <div className="wsipRowTrailing">
                          {isRefMultiSelect && <span className="wsipRow__checkbox" aria-hidden="true">{selected && <CheckRoundedIcon fontSize="inherit" />}</span>}
                          {attached && <span className="wsipAttachedBadge">Attached</span>}
                          {attached && (
                            <div className="wsipActionMenuWrap">
                              <IconButton
                                variant="tertiary"
                                size="sm"
                                label={`Actions for ${item.name}`}
                                icon={<MoreHorizRoundedIcon fontSize="small" />}
                                onClick={() => setOpenRowMenuId(openRowMenuId === item.id ? null : item.id)}
                              />
                              {openRowMenuId === item.id && (
                                <div className="wsipDropdownMenu">
                                  {canEdit && (
                                    <Pressable
                                      className="wsipDropdownItem"
                                      onClick={() => void handleDetach('stock', item.id)}
                                    >
                                      <LinkOffRoundedIcon fontSize="small" />
                                      <span>Detach from this item</span>
                                    </Pressable>
                                  )}
                                  {isAdmin && (
                                    <Pressable
                                      className="wsipDropdownItem isDanger"
                                      onClick={() => handleDeleteRequest('stock', item.id, item.name)}
                                    >
                                      <DeleteOutlineRoundedIcon fontSize="small" />
                                      <span>Delete permanently</span>
                                    </Pressable>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })
                )
              ) : activeKind === 'timeline' ? (
                filteredTree.length === 0 ? (
                  <p className="wsipEmpty">No items match.</p>
                ) : (
                  filteredTree.map((node) => (
                    <PickerTreeRow
                      key={node.id}
                      node={node}
                      depth={0}
                      onPick={handleSelectRow}
                      disabledIds={disabledIds}
                      selectedIds={selectedIds}
                      activeGoalRootId={activeGoalId}
                      currentGoalRootId={node.id}
                      forceExpanded={searchQuery.trim().length > 0}
                    />
                  ))
                )
              ) : activeKind === 'members' ? (
                (members ?? []).filter(m => !searchQuery || (m.displayName && m.displayName.toLowerCase().includes(searchQuery.toLowerCase())) || (m.email && m.email.toLowerCase().includes(searchQuery.toLowerCase()))).length === 0 ? (
                  <p className="wsipEmpty">No members match.</p>
                ) : (
                  (members ?? []).filter(m => !searchQuery || (m.displayName && m.displayName.toLowerCase().includes(searchQuery.toLowerCase())) || (m.email && m.email.toLowerCase().includes(searchQuery.toLowerCase()))).map(m => {
                    const ref: WorkspaceItemReference = { id: m.id, label: m.displayName, kind: 'member', parentId: null, ancestors: [] }
                    const selected = selectedRefIds.has(m.id)
                    const selectionDisabled = isFlatSelectionDisabled(m.id)
                    return (
                      <Pressable
                        key={m.id}
                        className={`wsipRowItem${isRefMultiSelect ? ' wsipRow--multi' : ''}${selected ? ' isSelected' : ''}`}
                        disabled={selectionDisabled}
                        onClick={() => handleFlatRowClick(ref)}
                      >
                        <OverflowTip label={m.displayName}>
                          <span className="wsipRowTitle">{m.displayName}</span>
                        </OverflowTip>
                        {m.email && <span className="wsipNoticeCaption" style={{marginLeft: 8}}>{m.email}</span>}
                        {isRefMultiSelect && <span className="wsipRow__checkbox" aria-hidden="true">{selected && <CheckRoundedIcon fontSize="inherit" />}</span>}
                      </Pressable>
                    )
                  })
                )
              ) : activeKind === 'chats' ? (
                <ChatsPane basket={basket} onToggle={toggleContext} />
              ) : activeKind === 'new_file' ? (
                <NewFileIngestPane onAdd={addContext} />
              ) : activeKind === 'gsheet' ? (
                <GoogleSheetPane onAdd={addContext} />
              ) : activeKind === 'image' ? (
                <ImageUploadPane
                  onFiles={(pickedFiles) => {
                    onFiles?.(pickedFiles)
                    onClose?.()
                  }}
                />
              ) : null}
            </div>
          </main>
        </div>

        {/* Footer */}
        {mode === 'aiContext' ? (
          <footer className="wsipFooter wsipFooter--ai">
            <div className="wsipBasketArea">
              {basket.map((item) => (
                <span key={item.id} className="wsipBasketChip">
                  <span>{item.label}</span>
                  <Pressable
                    className="wsipBasketChipRemove"
                    onClick={() => setBasket((current) => current.filter((ctx) => ctx.id !== item.id))}
                    aria-label={`Remove ${item.label}`}
                  >
                    <CloseRoundedIcon style={{ fontSize: 13 }} />
                  </Pressable>
                </span>
              ))}
              {basket.length === 0 && (
                <span className="wsipBasketEmptyText">No items selected</span>
              )}
            </div>

            <div className="wsipFooterActions">
              <span className="wsipCostEstimate">
                {costEstimate.fixedIngestCr} cr ·{' '}
                <strong>
                  {costEstimate.tokenRange.low}–{costEstimate.tokenRange.high} tok
                </strong>{' '}
                est.
              </span>
              <Button
                variant="primary"
                size="md"
                disabled={basket.length === 0}
                onClick={() => {
                  onCommit?.(basket)
                  onClose?.()
                }}
              >
                {basket.length === 0
                  ? 'Attach'
                  : basket.length === 1
                    ? 'Attach 1 item'
                    : `Attach ${basket.length} items`}
              </Button>
            </div>
          </footer>
        ) : isRefMultiSelect ? (
          <footer className="wsipFooter wsipFooter--ai wsipFooter--multi">
            <div className="wsipBasketArea">
              {selectedRefs.map((ref) => (
                <span key={ref.id} className="wsipBasketChip">
                  <span>{ref.label}</span>
                  <Pressable
                    className="wsipBasketChipRemove"
                    onClick={() => toggleSelectedRef(ref)}
                    aria-label={`Remove ${ref.label}`}
                  >
                    <CloseRoundedIcon style={{ fontSize: 13 }} />
                  </Pressable>
                </span>
              ))}
              {selectedRefs.length === 0 && (
                <span className="wsipBasketEmptyText">No items selected</span>
              )}
            </div>

            <div className="wsipFooterActions">
              {maxSelect !== undefined && (
                <span className="wsipCapCounter">{selectedRefs.length} / {maxSelect} selected</span>
              )}
              <Button
                variant="primary"
                size="md"
                disabled={selectedRefs.length === 0}
                onClick={() => {
                  onCommitRefs(selectedRefs)
                  onClose?.()
                }}
              >
                {selectedRefs.length === 0 ? 'Add' : `Add ${selectedRefs.length} item${selectedRefs.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </footer>
        ) : (
          <footer className="wsipFooter">
            <div className="wsipCounter">{totalAttachedCount} attached to this item</div>
            {onClose && (
              <Button variant="primary" size="md" onClick={onClose}>
                Done
              </Button>
            )}
          </footer>
        )}

        {/* Delete Confirmation Dialog */}
        <AppDialog
          open={Boolean(itemPendingDelete)}
          title={`Delete ${itemPendingDelete?.name || 'item'}?`}
          onDismiss={() => setItemPendingDelete(null)}
          actions={[
            {
              label: 'Cancel',
              variant: 'ghost',
              onClick: () => setItemPendingDelete(null),
            },
            {
              label: 'Delete',
              variant: 'danger',
              onClick: () => void handleConfirmDelete(),
            },
          ]}
          message={
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-strong)', lineHeight: 1.5 }}>
              This removes it from the whole workspace.
            </p>
          }
        />

        {/* Cross-Hub Confirmation Dialog */}
        {pendingLeaveHub && (
          <LeaveForHubDialog
            open={Boolean(pendingLeaveHub)}
            hubKey={pendingLeaveHub.hubKey}
            hubName={pendingLeaveHub.hubName}
            onConfirm={() => {
              const path = pendingLeaveHub.path
              setPendingLeaveHub(null)
              window.history.pushState(null, '', toConsolePath(path))
              window.dispatchEvent(new PopStateEvent('popstate'))
              onClose?.()
            }}
            onCancel={() => setPendingLeaveHub(null)}
          />
        )}
      </div>
    )
  }

  // If open prop is passed directly, wrap in the requested host.
  if (typeof open === 'boolean') {
    if (mobileHost === 'sheet') {
      return (
        <BottomSheet isOpen={open} onClose={() => onClose?.()} title={title ?? 'Attach provisions'} className="workspaceItemPickerSheet">
          {renderContent()}
        </BottomSheet>
      )
    }
    return (
      <AppDialog
        open={open}
        size="attachPicker"
        title={title ?? 'Attach provisions'}
        onDismiss={onClose}
        actions={[]}
        message={renderContent()}
      />
    )
  }

  return renderContent()
}
